/* ══════════════════════════════════════════════════════════════════
   Eine Wahrheit über die Miettage — Durchgang 03.10.2026
   (V-15N, V-06, L-01-N1, AG-30).

   Der Kopf der Vermietfläche zählte nach dem Kalender des Betriebs
   (fünf Tage), jede Trefferzeile nach dem Kalender ihrer Gerätegruppe — und
   gerechnet wird nach dem der Gruppe. „Dauer: 2 Miettage" ab Sonntag hieß
   oben 2, an der Zeile und im Auftrag 3 (Rüttler 35/60D, Kleingeräte mit
   sieben Tagen: 3 × 30,00 statt 2 × …). 06.–15.10. hieß oben 8, an der Zeile
   10. Der Kalender, der rechnet, ist der, der zählt.

   Zwei Regeln, beide ohne Browser prüfbar:

   1. **Wer eine Dauer nennt, fragt jede Zeile nach dieser Dauer** — nicht
      nach dem Ende, das der Betriebskalender daraus macht. Das Ende rechnet
      dann jeder Typ mit seinem eigenen Kalender (`GET /preise?miettage=`),
      und n Miettage bleiben n.
   2. **Der Kopf nennt eine Zahl nur, wenn sie für die Zeilen gilt** — die
      der Zeilen, wenn alle dieselbe tragen; vor den ersten Zeilen die des
      Betriebs; tragen sie verschiedene, keine (jede Zeile nennt ihre).
   ══════════════════════════════════════════════════════════════════ */

/**
 * Wonach die Preise gefragt werden — **die eine Frage für alle Stellen**
 * der Vermietfläche und des Nachschlagens: Trefferzeile, „Merken",
 * „Wie zuletzt" und die Liste darunter (Prüfrunde Runde 10, N10-01).
 *
 * **Eine Dauer ist nur, was jemand als Dauer gefragt hat** (`art: "dauer"`,
 * am Schreibtisch). Die Vorbelegung „ab morgen, eine Arbeitswoche" steht als
 * **Zeitraum** da (`art: "spanne"` ohne eigenes Ende; das Ende rechnet der
 * Kalender des Betriebs — Di 06.10. – Mo 12.10.), am Telefon gibt es ohnehin
 * nur den Zeitraum. Bis zum 05.10.2026 fragte die Trefferzeile hier nach
 * `miettage=5`, weil in der Vorbelegung `tage: 5` mitstand: Der Rüttler
 * (Kleingeräte, sieben Tage) rechnete 06.–10.10. = 5 × 30,00, „Wie zuletzt"
 * darüber fragte nach dem gezeigten Ende 12.10. = 7 × 22,00 — zwei Preise
 * für denselben Kopf. Jetzt gilt der Zeitraum, der oben steht, und jeder
 * Typ zählt darin seine Miettage nach seinem Mietkalender.
 */
export function zeilenfrage(
  wunsch: { art?: string | undefined; bis?: string | null | undefined; tage?: number | null | undefined },
  bisAusKalender: string | null,
): { bis: string } | { miettage: number } | null {
  if (wunsch.bis) return { bis: wunsch.bis };
  if (wunsch.art === "dauer" && wunsch.tage && wunsch.tage > 0) return { miettage: wunsch.tage };
  return bisAusKalender ? { bis: bisAusKalender } : null;
}

/**
 * **Die Miettage, die eine Dauer meint** — `null`, wenn ein Zeitraum gefragt
 * ist. Für die Stellen, die nicht selbst nach Preisen fragen, aber dasselbe
 * Ende eintragen müssen wie die Trefferzeile („Wie zuletzt",
 * `bausteine/Kundenverlauf.tsx`): Bei einer Dauer rechnet jeder Typ sein
 * eigenes Ende, sonst gilt das Ende des Zeitraums.
 */
export function dauerAusFrage(frage: { bis: string } | { miettage: number } | null): number | null {
  return frage && "miettage" in frage ? frage.miettage : null;
}

/**
 * Was im Kopf neben dem Zeitraum steht.
 *
 * `gemischt` heißt: Die Zeilen zählen nach verschiedenen Mietkalendern —
 * dann steht oben keine Zahl, aber auch kein „lädt" (N10-01: seit die
 * Vorbelegung ein Zeitraum ist, ist eine gemischte Liste der Alltag).
 * `bis` ist das Ende, das für alle Zeilen gilt — bei einer Dauer rechnet
 * jeder Typ seins, und gehen sie auseinander, nennt der Kopf keins.
 */
export function kopfMiettage<S>(
  betrieb: { miettage: number; schritte: S[]; bis?: string | undefined } | null,
  zeilen: readonly { miettage: number; bis?: string | undefined; ohnePreis?: string | undefined }[] | null,
): { miettage: number | null; schritte: S[]; gemischt: boolean; bis: string | null } {
  if (!betrieb) return { miettage: null, schritte: [], gemischt: false, bis: null };
  const gerechnet = (zeilen ?? []).filter((z) => !z.ohnePreis);
  if (gerechnet.length === 0) {
    return { miettage: betrieb.miettage, schritte: betrieb.schritte, gemischt: false, bis: betrieb.bis ?? null };
  }
  const enden = [...new Set(gerechnet.map((z) => z.bis ?? betrieb.bis ?? ""))];
  const bis = enden.length === 1 && enden[0] ? enden[0] : null;
  const werte = [...new Set(gerechnet.map((z) => z.miettage))];
  if (werte.length !== 1) return { miettage: null, schritte: [], gemischt: true, bis };
  const wert = werte[0] as number;
  // Der Rechenweg des Betriebs erklärt nur seine eigene Zahl.
  return { miettage: wert, schritte: wert === betrieb.miettage ? betrieb.schritte : [], gemischt: false, bis };
}
